import { Plus, Trash2 } from 'lucide-react';
import type { OpcoesCotacao } from '../../lib/cotacao';

export interface PecaAcrilico {
  largura: string;
  altura: string;
  tipo: string;
  cor: string;
  espessura: string;
  qtd: string;
}

export interface CamposAcr {
  pecas: PecaAcrilico[];
  acabamentos: string[];
  gabarito: string;
}

type Opcoes = NonNullable<OpcoesCotacao['acrilico']>;

const num = (s: string) => Number(s.replace(',', '.'));

export function novaPeca(o: Opcoes): PecaAcrilico {
  const tipo = o.tipos[0] ?? '';
  return {
    largura: String(o.larguras[0] ?? ''),
    altura: '',
    tipo,
    cor: o.cores[tipo]?.[0] ?? '',
    espessura: '2mm',
    qtd: '1',
  };
}

export const pecaCompleta = (p: PecaAcrilico) =>
  num(p.largura) > 0 && num(p.altura) > 0 && !!p.tipo;

export function CamposAcrilico({
  o,
  f,
  mudar,
  valores,
}: {
  o: Opcoes;
  f: CamposAcr;
  mudar: (p: Partial<CamposAcr>) => void;
  valores: Map<number, number>;
}) {
  const mudarPeca = (i: number, p: Partial<PecaAcrilico>) =>
    mudar({ pecas: f.pecas.map((x, j) => (j === i ? { ...x, ...p } : x)) });

  const trocarTipo = (i: number, tipo: string) => {
    const atual = f.pecas[i]!;
    mudarPeca(i, {
      tipo,
      cor: o.cores[tipo]?.[0] ?? '',
      espessura: tipo === 'Transparente' ? atual.espessura : '2mm',
      largura:
        tipo !== 'Transparente' && !o.larguras.includes(num(atual.largura))
          ? String(o.larguras[0])
          : atual.largura,
    });
  };

  const alternarAcabamento = (a: string) =>
    mudar({
      acabamentos: f.acabamentos.includes(a)
        ? f.acabamentos.filter((x) => x !== a)
        : [...f.acabamentos, a],
    });

  let indiceCompleta = -1;
  return (
    <>
      <div className="campo">
        <span>Peças</span>
        <div className="pecas-acr">
          {f.pecas.map((p, i) => {
            if (pecaCompleta(p)) indiceCompleta++;
            const valor = pecaCompleta(p) ? valores.get(indiceCompleta) : undefined;
            const cores = o.cores[p.tipo] ?? [];
            return (
              <div key={i} className="peca-acr">
                <div className="peca-topo">
                  <b>Peça {i + 1}</b>
                  {valor !== undefined && (
                    <span className="peca-valor">
                      {valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  )}
                  {f.pecas.length > 1 && (
                    <button
                      className="icone-btn sem-borda perigo"
                      aria-label={`Remover peça ${i + 1}`}
                      onClick={() => mudar({ pecas: f.pecas.filter((_, j) => j !== i) })}
                    >
                      <Trash2 />
                    </button>
                  )}
                </div>
                <div className="segmentos pequenos" role="radiogroup" aria-label="Tipo">
                  {o.tipos.map((t) => (
                    <button
                      key={t}
                      role="radio"
                      aria-checked={p.tipo === t}
                      className={p.tipo === t ? 'ativo' : ''}
                      onClick={() => trocarTipo(i, t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <div className="peca-grade">
                  <label className="campo">
                    <span>Largura (cm)</span>
                    {p.tipo === 'Transparente' ? (
                      <input
                        className="input"
                        inputMode="decimal"
                        value={p.largura}
                        onChange={(e) => mudarPeca(i, { largura: e.target.value })}
                      />
                    ) : (
                      <select
                        className="input"
                        value={p.largura}
                        onChange={(e) => mudarPeca(i, { largura: e.target.value })}
                      >
                        {o.larguras.map((l) => (
                          <option key={l} value={l}>
                            {l} cm
                          </option>
                        ))}
                      </select>
                    )}
                  </label>
                  <label className="campo">
                    <span>Altura (cm)</span>
                    <input
                      className="input"
                      inputMode="decimal"
                      value={p.altura}
                      onChange={(e) => mudarPeca(i, { altura: e.target.value })}
                    />
                  </label>
                  {p.tipo === 'Transparente' ? (
                    <label className="campo">
                      <span>Espessura</span>
                      <select
                        className="input"
                        value={p.espessura}
                        onChange={(e) => mudarPeca(i, { espessura: e.target.value })}
                      >
                        {o.espessurasTransparente.map((e) => (
                          <option key={e}>{e}</option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <label className="campo">
                      <span>Cor</span>
                      <select
                        className="input"
                        value={p.cor}
                        onChange={(e) => mudarPeca(i, { cor: e.target.value })}
                      >
                        {cores.map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label className="campo">
                    <span>Qtd</span>
                    <input
                      className="input"
                      inputMode="numeric"
                      value={p.qtd}
                      onChange={(e) => mudarPeca(i, { qtd: e.target.value.replace(/\D/g, '') })}
                    />
                  </label>
                </div>
              </div>
            );
          })}
          <button className="btn" onClick={() => mudar({ pecas: [...f.pecas, novaPeca(o)] })}>
            <Plus /> Adicionar peça
          </button>
        </div>
      </div>

      {o.acabamentos.length > 0 && (
        <div className="campo">
          <span>Acabamentos</span>
          <div className="chips">
            {o.acabamentos.map((a) => (
              <button
                key={a}
                aria-pressed={f.acabamentos.includes(a)}
                className={f.acabamentos.includes(a) ? 'ativo' : ''}
                onClick={() => alternarAcabamento(a)}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
      )}

      {o.gabaritos.length > 0 && (
        <label className="campo">
          <span>Gabarito</span>
          <select
            className="input"
            value={f.gabarito}
            onChange={(e) => mudar({ gabarito: e.target.value })}
          >
            <option value="">Sem gabarito</option>
            {o.gabaritos.map((g) => (
              <option key={g}>{g}</option>
            ))}
          </select>
        </label>
      )}
    </>
  );
}
